export const SERVICE_PACKAGE_CHECKOUT_FLOW = 'service_package';

export const MAX_PACKAGE_LINE_ITEMS = 10;

export const PACKAGE_BOOKING_CREDIT_EXPIRY_DAYS = 90;

export const PackageLineRefType = {
  SERVICE_OFFER: 'service_offer',
  COURSE: 'course',
  MEMBERSHIP_TIER: 'membership_tier',
  EVENT: 'event',
} as const;

export type PackageLineRefTypeValue =
  (typeof PackageLineRefType)[keyof typeof PackageLineRefType];

export type PackageLineItemInput = {
  includedOfferId?: string;
  includedCourseId?: string;
  includedMembershipTierId?: string;
  includedEventId?: string;
  quantity?: number;
  sortOrder?: number;
};

export function packageLineRefKey(item: PackageLineItemInput): string | null {
  if (item.includedOfferId) return `${PackageLineRefType.SERVICE_OFFER}:${item.includedOfferId}`;
  if (item.includedCourseId) return `${PackageLineRefType.COURSE}:${item.includedCourseId}`;
  if (item.includedMembershipTierId) {
    return `${PackageLineRefType.MEMBERSHIP_TIER}:${item.includedMembershipTierId}`;
  }
  if (item.includedEventId) return `${PackageLineRefType.EVENT}:${item.includedEventId}`;
  return null;
}

export function validatePackageLineItems(items: PackageLineItemInput[]): string[] {
  const errors: string[] = [];
  if (items.length === 0) errors.push('Add at least one included item');
  if (items.length > MAX_PACKAGE_LINE_ITEMS) {
    errors.push(`At most ${MAX_PACKAGE_LINE_ITEMS} line items allowed`);
  }
  const refs = new Set<string>();
  for (const item of items) {
    const refCount = [
      item.includedOfferId,
      item.includedCourseId,
      item.includedMembershipTierId,
      item.includedEventId,
    ].filter(Boolean).length;
    if (refCount !== 1) {
      errors.push('Each line item must reference exactly one catalog item');
      continue;
    }
    const key = packageLineRefKey(item);
    if (!key) continue;
    if (refs.has(key)) {
      errors.push('Each included item can only appear once');
    }
    refs.add(key);
    const qty = item.quantity ?? 1;
    if (qty < 1 || qty > 20) errors.push('Line item quantity must be 1–20');
  }
  return errors;
}

export function validatePackageOfferPublish(input: {
  title: string;
  priceCents: number;
  refundPolicyText?: string | null;
  lineItems: PackageLineItemInput[];
}): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!input.title.trim()) errors.push('Title is required');
  if (input.priceCents > 0 && !input.refundPolicyText?.trim()) {
    errors.push('Refund policy is required for paid offers');
  }
  errors.push(...validatePackageLineItems(input.lineItems));

  return { errors, warnings };
}

export function packageCreditExpiresAt(from = new Date()): Date {
  return new Date(from.getTime() + PACKAGE_BOOKING_CREDIT_EXPIRY_DAYS * 24 * 3600_000);
}

export const SERVICE_PACKAGE_ANALYTICS_EVENT = {
  PURCHASED: 'package.purchased',
} as const;
